// src/pages/BankTransaction/components/CsvUploadPage.jsx
import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";
import { Table, Button, Select, message, Spin, Grid } from "antd";
import { UploadOutlined, SaveOutlined } from "@ant-design/icons";
import Papa from "papaparse";
import Swal from "sweetalert2"; // ✅ SweetAlert2
import { getAccountBySeachApi } from "../../../apiservice/setting";
import { postData, fetchData } from "../../../apiservice/apis";
import { getAccessType } from "../../../lib/functions";

const { Option } = Select;

const CsvUploadPage = ({ authInfo }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const fileInputRef = useRef(null);
  let [access_type, access_type_set] = useState("");

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  useEffect(() => {
    const access = getAccessType("bank_transactions");
    access_type_set(access);
  }, []);

  // 🔹 Fetch accounts for dropdown
  useEffect(() => {
    getAccountBySeachApi({
      multiType: ["cash_in_hand", "bank_account"],
    })
      .then((res) => setAccounts(res.data || []))
      .catch(() => {
        Swal.fire({
          title: "Failed to load accounts",
          icon: "error",
        });
      });
  }, []);

  const [lastReconciledDate, setLastReconciledDate] = useState(null);

  useEffect(() => {
    const fetchLastReconciled = async () => {
      try {
        const res = await fetchData(
          `/accounts/last-reconciled-status?branch_id=${authInfo.userInfo.branch_id}&created_by=${authInfo.userInfo.user_id}&account_id=${selectedAccount}`
        );
        setLastReconciledDate(res.data?.data?.last_reconciled_date || null);
      } catch (err) {
        console.error("Failed to fetch last reconciled date", err);
      }
    };
    if (selectedAccount) fetchLastReconciled();
  }, [selectedAccount]);

  const formatCsvDate = (input) => {
    if (!input) return null;
    const momentFormats = ["DD/MM/YYYY", "D/M/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"];
    const parsed = dayjs(input.trim(), momentFormats, true);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
  };

  const validateAgainstReconciliation = (rows) => {
    if (!lastReconciledDate) return rows; // no restriction yet

    const invalidRows = rows.filter((row) => {
      const parsedDate = formatCsvDate(row.Date);
      return parsedDate && dayjs(parsedDate).isBefore(dayjs(lastReconciledDate));
    });

    if (invalidRows.length > 0) {
      Swal.fire({
        title: "Invalid Upload",
        text: `You cannot upload transactions dated before the last reconciled date: ${dayjs(lastReconciledDate).format("DD-MM-YYYY")}`,
        icon: "error",
      });
      return null;
    }

    return rows;
  };



  // 🔹 CSV File change handler
  const handleFileChange = (e) => {
    const uploadedFile = e.target.files[0];
    if (!uploadedFile) return;

    if (
      uploadedFile.type !== "text/csv" &&
      !uploadedFile.name.endsWith(".csv")
    ) {
      Swal.fire({
        title: "Invalid file type",
        text: "Please upload a CSV file.",
        icon: "error",
      });
      return;
    }

    setIsLoading(true);

    Papa.parse(uploadedFile, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        let cleaned = results.data.filter(
          (row) =>
            Object.values(row).some((val) => val && String(val).trim() !== "")
        );

        // ✅ Validate against last reconciled date
        const validData = validateAgainstReconciliation(cleaned);
        if (!validData) {
          setFile(null);
          setPreviewData([]);
          return;
        }

        setPreviewData(validData);
        setFile(uploadedFile);
        setIsLoading(false);
      },
    });


  };

  // 🔹 Save handler with confirmation + toast
  const handleSave = async () => {
    if (!selectedAccount) {
      return Swal.fire({
        title: "Missing Account",
        text: "Select an account before saving",
        icon: "warning",
      });
    }
    if (!file) {
      return Swal.fire({
        title: "Missing File",
        text: "Please upload a CSV file first",
        icon: "warning",
      });
    }

    const confirm = await Swal.fire({
      title: "Confirm Save",
      text: "Do you want to save these transactions?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    const formData = new FormData();
    formData.append("file", file);
    formData.append("branch_id", authInfo.userInfo.branch_id);
    formData.append("user_id", authInfo.userInfo.user_id);
    formData.append("selected_account", selectedAccount);

    try {
      const res = await postData("/accounts/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      // ✅ success toast
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "success",
        title: res.data.message || "CSV uploaded successfully",
        showConfirmButton: false,
        timer: 2500,
      });

      // reset
      setFile(null);
      setPreviewData([]);
      setSelectedAccount(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      // ❌ error toast
      Swal.fire({
        toast: true,
        position: "bottom-end",
        icon: "error",
        title: err.response?.data?.message || "Failed to save transactions",
        showConfirmButton: false,
        timer: 3000,
      });
    }
  };

  const columns =
    previewData.length > 0
      ? Object.keys(previewData[0]).map((key) => ({
        title: key,
        dataIndex: key,
        key,
      }))
      : [];

  return (
    <div style={{ padding: screens.xs ? "0rem" : "2rem" }}>
      <h3>📂 Upload Bank Transactions</h3>

      <div style={{ display: "flex", flexDirection: screens.xs ? "column" : "row" , gap: "1rem", marginBottom: "1rem" }}>

        {access_type == "WRITE" && <>
          <Button
            icon={<UploadOutlined />}
            type="primary"
            onClick={() => fileInputRef.current.click()}
          >
            Upload CSV
          </Button>
        </>}

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".csv"
          style={{ display: "none" }}
        />

        <Select
          placeholder="Select Account"
          value={selectedAccount}
          onChange={setSelectedAccount}
          style={{ width: 200 }}
        >
          {accounts.map((acc) => (
            <Option key={acc.acc_id} value={acc.acc_id}>
              {acc.acc_name}
            </Option>
          ))}
        </Select>

        <Button
          icon={<SaveOutlined />}
          type="primary"
          onClick={handleSave}
          disabled={!previewData.length}
        >
          Save Transactions
        </Button>
      </div>

      {isLoading && <Spin />}

      {previewData.length > 0 && (
        <Table
          dataSource={previewData}
          columns={columns}
          rowKey={(row, index) => index}
          pagination={{
            defaultPageSize: 30,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "30", "50", "100"],
            onChange: () => {
              // Auto scroll to top on page change
              window.scrollTo({ top: 0, behavior: "smooth" });
            },
          }}
          bordered
        />

      )}
    </div>
  );
};

export default CsvUploadPage;
